<script>
/**
 * @component PlanList
 * @description Listado de planes de cobertura con filtros, búsqueda, tabla paginada,
 *              dialog de alta/edición y navegación al detalle de cada plan.
 * @example <PlanList />
 */
import planService from "@/services/plan";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const TYPE_OPTIONS = [
  { value: "prepaid", label: "Prepaga" },
  { value: "social", label: "Obra Social" },
  { value: "pmo", label: "PMO" },
  { value: "complementary", label: "Complementario" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
];

export default {
  name: "PlanList",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterType: "",
      plans: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      typeOptions: TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      // Dialog
      isDialogVisible: false,
      form: {
        code: "",
        name: "",
        type: "",
        monthlyFee: "",
        description: "",
      },
      formErrors: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  created() {
    this.fetchPlans();
  },

  methods: {
    /**
     * @description Obtiene los planes del backend con filtros aplicados
     */
    async fetchPlans() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterType) params.type = this.filterType;

        const result = await planService.query(params);
        this.plans = result.docs || [];
        this.total = result.total || 0;
      } catch (error) {
        this.$message.error("Error al cargar planes");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchPlans();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchPlans();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPlans();
    },

    /**
     * @description Devuelve el label del tipo de plan
     * @param {string} type - Valor
     * @returns {string}
     */
    getTypeLabel(type) {
      const opt = TYPE_OPTIONS.find((o) => o.value === type);
      return opt ? opt.label : type || "-";
    },

    /**
     * @description Devuelve la clase badge del estado
     * @param {string} status - Valor
     * @returns {string}
     */
    getStatusBadge(status) {
      return status === "active" ? "badge--success" : "badge--neutral";
    },

    /**
     * @description Devuelve el label del estado
     * @param {string} status - Valor
     * @returns {string}
     */
    getStatusLabel(status) {
      const opt = STATUS_OPTIONS.find((o) => o.value === status);
      return opt ? opt.label : status || "-";
    },

    /**
     * @description Formatea un número como moneda AR
     * @param {number} value - Valor
     * @returns {string}
     */
    formatCurrency(value) {
      return `$${(value || 0).toLocaleString("es-AR")}`;
    },

    /**
     * @description Navega al detalle del plan
     * @param {Object} plan - Plan seleccionado
     */
    goToDetail(plan) {
      this.$router.push({ name: "plan-detail", params: { id: plan._id } });
    },

    // --- Dialog ---

    /**
     * @description Abre el dialog para crear un nuevo plan
     */
    openDialog() {
      this.isDialogVisible = true;
      this.formErrors = {};
      this.form = {
        code: "",
        name: "",
        type: "",
        monthlyFee: "",
        description: "",
      };
    },

    closeDialog() {
      this.isDialogVisible = false;
      this.formErrors = {};
    },

    /**
     * @description Valida que code y name estén completos
     * @returns {boolean} true si es válido
     */
    validateForm() {
      this.formErrors = {};
      if (!this.form.code.trim()) {
        this.formErrors.code = "Código requerido";
      }
      if (!this.form.name.trim()) {
        this.formErrors.name = "Nombre requerido";
      }
      return Object.keys(this.formErrors).length === 0;
    },

    /**
     * @description Guarda el plan nuevo y navega al detalle
     */
    async savePlan() {
      if (!this.validateForm()) return;

      try {
        const payload = {
          code: this.form.code.trim(),
          name: this.form.name.trim(),
          status: "active",
        };
        if (this.form.type) payload.type = this.form.type;
        if (this.form.monthlyFee) {
          payload.monthlyFee = Number(this.form.monthlyFee);
        }
        if (this.form.description) {
          payload.description = this.form.description.trim();
        }

        await planService.create(payload);
        this.$message.success("Plan creado exitosamente");
        this.closeDialog();
        this.fetchPlans();
      } catch (error) {
        this.$message.error(
          error.response?.data?.error || "Error al crear plan"
        );
      }
    },
  },
};
</script>

<template lang="pug">
section
  //- Filters
  header.headline
    .headline__actions
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por código o nombre"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterType"
        placeholder="Tipo"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in typeOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      .button.button--navy(@click="openDialog" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Plan

  //- Table
  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Código
            th Nombre
            th Tipo
            th Cuota Mensual
            th Estado
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="p in plans"
            :key="p._id"
            @click="goToDetail(p)"
          )
            td {{ p.code || '-' }}
            td {{ p.name }}
            td {{ getTypeLabel(p.type) }}
            td {{ formatCurrency(p.monthlyFee) }}
            td
              span.badge(:class="getStatusBadge(p.status)") {{ getStatusLabel(p.status) }}

    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="onPageChange"
    )

  //- Create dialog
  el-dialog(
    title="Nuevo Plan"
    :visible.sync="isDialogVisible"
    width="620px"
    :close-on-click-modal="false"
  )
    .plan-form
      .form-section
        .form-section__title Datos del plan
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  Código
            el-input(v-model="form.code" size="small" maxlength="30" placeholder="Ej: PLAN-500")
            span.has-error(v-if="formErrors.code") {{ formErrors.code }}
          .form-field
            label.form-field__label
              span.required *
              |  Nombre
            el-input(v-model="form.name" size="small" maxlength="100" placeholder="Ej: Plan 500")
            span.has-error(v-if="formErrors.name") {{ formErrors.name }}
        .form-row
          .form-field
            label.form-field__label Tipo
            el-select(v-model="form.type" size="small" placeholder="Seleccionar tipo" clearable)
              el-option(v-for="opt in typeOptions" :key="opt.value" :label="opt.label" :value="opt.value")
          .form-field
            label.form-field__label Cuota mensual
            el-input(v-model="form.monthlyFee" size="small" type="number" placeholder="Ej: 15000")
        .form-row.form-row--1col
          .form-field
            label.form-field__label Descripción
            el-input(v-model="form.description" size="small" type="textarea" :rows="3" placeholder="Descripción del plan")

    div(slot="footer")
      el-button(@click="closeDialog" size="small") Cancelar
      el-button(type="primary" @click="savePlan" size="small") Guardar
</template>

<style lang="scss" scoped>
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.search__input {
  width: 280px;
}
.el-select {
  width: 160px;
}
tbody tr {
  cursor: pointer;
}

// ─── Plan form (dialog) ───
.plan-form {
  padding: 0 4px;
}

.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  background: white;

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 16px;
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 12px;

  &--1col {
    grid-template-columns: 1fr;
  }
}

.form-field {
  min-width: 0;

  .el-select,
  .el-date-editor {
    width: 100%;
  }
}

.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;

  .required {
    color: #ef4444;
    margin-right: 2px;
  }
}

.has-error {
  color: #ef4444;
  font-size: 12px;
}
</style>
